import styled from '@emotion/styled';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import UserMultiple from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import React from 'react';
import { useParams } from 'react-router-dom';

import CreateReportButton from './create-report-button';
import { useDashboardDataContext } from './dashboard/dashboard-context';
import { isUserProjectLeadOrOwner } from './right-panel';
import Spacer from '../base/spacer';
import UserNavigationMenu from '../settings/user-navigation-menu';

import { DesignToken } from '~/client/components/base/core';
import { useUserProfile } from '~/client/hooks/user-profile';
import * as GQL from '~/graphql/generated/client';
import { MedicalHeartrateReport, FileDocument } from '~c/components/base/icon';
import {
  LeftNavMenu,
  LeftNavMenuItem,
  LeftNavMenuGroup,
  LeftNavFlyoutMenuItem,
  useLeftNavContext,
} from '~c/components/base/left-nav';
import { ProjectDisplayName } from '~c/components/project/project-name-left-nav';
import { aiDashboardUtils, functionalModelsRouteUtil } from '~c/routes';

interface Props {
  className?: string;
}

const CandidateModelsLeftNavImpl: React.FC<Props> = ({ className }) => {
  const { matchRoute, navigateTo } = functionalModelsRouteUtil.useHelper();
  const [reportsExpanded, setReportsExpanded] = React.useState(false);
  const { projectId } = useParams<{ projectId: string }>();

  const dashboard = aiDashboardUtils.useHelper();
  const { project } = useDashboardDataContext();
  const { userProfile } = useUserProfile();

  // Check if user can manage settings
  const canManageSettings = isUserProjectLeadOrOwner(
    project as GQL.Project,
    userProfile?.email ?? ''
  );

  const { state } = useLeftNavContext();
  const { currentRoute } = state;

  const routePrefix = {
    [functionalModelsRouteUtil.routePrefix]:
      functionalModelsRouteUtil.routePrefix,
  };

  React.useEffect(() => {
    if (matchRoute('REPORT') || matchRoute('FUNCTIONAL_MODEL_REPORT')) {
      setReportsExpanded(true);
    }
  }, [matchRoute]);

  const handleReportsClick = () => {
    setReportsExpanded(!reportsExpanded);
  };

  return (
    <LeftNavMenu
      className={className}
      name={routePrefix[currentRoute ?? '']}
      header={{
        showBackButton: true,
        onClick: () => dashboard.navigateTo('FUNCTIONAL_MODELS_LIST'),
        // projectName: <FunctionModelName className={className} />,
      }}
    >
      <LeftNavMenuItem
        selected={matchRoute('DASHBOARD')}
        icon={DashboardOutlinedIcon}
        onClick={() => navigateTo('DASHBOARD')}
      >
        <ProjectDisplayName />
      </LeftNavMenuItem>
      <LeftNavMenuItem
        selected={matchRoute('CANDIDATES')}
        icon={ModelTrainingIcon}
        onClick={() => navigateTo('CANDIDATES')}
      >
        Models
      </LeftNavMenuItem>
      <LeftNavMenuItem
        selected={matchRoute('AGENTS')}
        icon={SmartToyIcon}
        onClick={() => navigateTo('AGENTS')}
      >
        Agents
      </LeftNavMenuItem>
      <LeftNavMenuItem
        selected={matchRoute('DATASETS')}
        icon={UserMultiple}
        onClick={() => navigateTo('DATASETS')}
      >
        Datasets
      </LeftNavMenuItem>
      <LeftNavMenuItem
        selected={false}
        icon={UserMultiple}
        onClick={() => dashboard.navigateTo('PROJECT_MEMBERS')}
      >
        Stakeholders
      </LeftNavMenuItem>
      <LeftNavMenuItem
        selected={matchRoute('DOCUMENTS')}
        icon={FolderIcon}
        onClick={() =>
          navigateTo('DOCUMENTS', {
            params: { projectId: projectId ?? '' },
          })
        }
      >
        Documents
      </LeftNavMenuItem>

      {/* Reports Menu Group */}
      <LeftNavMenuGroup
        icon={MedicalHeartrateReport}
        title="Reports"
        expandedIcon={ExpandLessIcon}
        collapsedIcon={ExpandMoreIcon}
        expanded={reportsExpanded}
        onToggle={handleReportsClick}
      >
        <LeftNavFlyoutMenuItem
          icon={MedicalHeartrateReport}
          selected={matchRoute('REPORT')}
          onClick={() => navigateTo('REPORT')}
        >
          Basic Reports
        </LeftNavFlyoutMenuItem>
        <LeftNavFlyoutMenuItem
          icon={FileDocument}
          selected={matchRoute('FUNCTIONAL_MODEL_REPORT')}
          onClick={() => navigateTo('FUNCTIONAL_MODEL_REPORT')}
        >
          Advanced Reports
        </LeftNavFlyoutMenuItem>
      </LeftNavMenuGroup>

      {/* Settings - only visible to project Owner */}
      {canManageSettings && (
        <LeftNavMenuItem
          selected={matchRoute('SETTINGS')}
          icon={SettingsIcon}
          onClick={() => navigateTo('SETTINGS')}
        >
          Settings
        </LeftNavMenuItem>
      )}
      <Spacer className="grow" />
      <UserNavigationMenu />
    </LeftNavMenu>
  );
};

const CandidateModelsLeftNav = styled(CandidateModelsLeftNavImpl)`
  .grow {
    flex-grow: 1;
  }
  .userinfo {
    padding: 16px;
    text-transform: capitalize;
  }
  .task-menu-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  .task-badge {
    background-color: ${DesignToken.status.danger};
    color: ${DesignToken.brand.primaryContrast};
    border-radius: 10px;
    padding: 2px 8px;
    font-size: 12px;
    margin-left: 8px;
    font-weight: 500;
  }

  .v2-header {
    margin-left: -10px;
  }

  .submenu-container {
    padding-left: 12px;
    margin-top: 4px;
    margin-bottom: 8px;
  }
`;

export default CandidateModelsLeftNav;
